import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, eur, errMsg } from "@/lib/api";

const input = "w-full h-10 px-3 bg-[#1F2230] border border-[#3B4157] text-sm outline-none focus:border-orange-500";
const today = () => new Date().toISOString().slice(0, 10);
const plus14 = () => new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10);
const EMPTY_BUYER = { name: "", company_code: "", vat_code: "", address: "", phone: "", email: "" };
const BUYER_FIELDS = [["name", "Pirkėjas (vardas, pavardė arba įmonė) *"], ["company_code", "Įmonės kodas"], ["vat_code", "PVM mokėtojo kodas"],
  ["address", "Adresas"], ["phone", "Telefonas"], ["email", "El. paštas"]];
const line = () => ({ name: "", code: "", qty: 1, unit: "vnt.", price: "" });

const ItemRow = ({ it, i, products, set, remove }) => (
  <div className="grid grid-cols-12 gap-2 items-center">
    <input data-testid={`invoice-item-name-${i}`} list="inv-products" placeholder="Prekė arba paslauga" value={it.name} className={`${input} col-span-12 sm:col-span-5`}
      onChange={(e) => { const p = products.find((x) => x.name === e.target.value); set(p ? { ...it, name: p.name, code: p.sku || "", price: p.price } : { ...it, name: e.target.value }); }} />
    <input data-testid={`invoice-item-code-${i}`} placeholder="Kodas" value={it.code} onChange={(e) => set({ ...it, code: e.target.value })} className={`${input} col-span-4 sm:col-span-2`} />
    <input data-testid={`invoice-item-qty-${i}`} type="number" min="0" step="1" value={it.qty} onChange={(e) => set({ ...it, qty: e.target.value })} className={`${input} col-span-2 sm:col-span-1`} />
    <input data-testid={`invoice-item-price-${i}`} type="number" min="0" step="0.01" placeholder="Kaina be PVM" value={it.price} onChange={(e) => set({ ...it, price: e.target.value })} className={`${input} col-span-4 sm:col-span-3`} />
    <button data-testid={`invoice-item-remove-${i}`} onClick={remove} className="col-span-2 sm:col-span-1 h-10 inline-flex items-center justify-center text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
  </div>
);

export default function InvoiceDialog({ open, onClose, onCreated }) {
  const [buyer, setBuyer] = useState(EMPTY_BUYER);
  const [items, setItems] = useState([line()]);
  const [date, setDate] = useState(today());
  const [paid, setPaid] = useState(true);
  const [due, setDue] = useState(plus14());
  const [note, setNote] = useState("");
  const [send, setSend] = useState(true);
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (!open) return;
    setBuyer(EMPTY_BUYER); setItems([line()]); setDate(today()); setPaid(true); setDue(plus14()); setNote(""); setSend(true);
    api.get("/admin/products").then((r) => setProducts(r.data));
  }, [open]);

  const valid = items.filter((i) => i.name.trim() && Number(i.qty) > 0 && i.price !== "");
  const net = valid.reduce((s, i) => s + Math.round(Number(i.price) * Number(i.qty) * 100) / 100, 0);
  const vat = Math.round(net * 21) / 100;

  const save = async () => {
    if (buyer.name.trim().length < 2) return toast.error("Įrašykite pirkėją");
    if (!valid.length) return toast.error("Pridėkite bent vieną prekę su kaina");
    if (send && !buyer.email) return toast.error("Norint išsiųsti, įrašykite pirkėjo el. paštą");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invoices", { buyer, items: valid.map((i) => ({ ...i, qty: Number(i.qty), price: Number(i.price) })),
        date, paid, due_date: paid ? "" : due, note, send });
      toast.success(`Sąskaita ${data.number} sukurta${send ? " ir išsiųsta" : ""}`);
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="invoice-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#292D3C] border-[#3B4157] text-slate-100">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wider">Nauja sąskaita</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {BUYER_FIELDS.map(([k, label]) => (
            <label key={k} className="text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>{label}</span>
              <input data-testid={`invoice-buyer-${k}`} value={buyer[k]} onChange={(e) => setBuyer({ ...buyer, [k]: e.target.value })} className={input} /></label>
          ))}
        </div>
        <datalist id="inv-products">{products.map((p) => <option key={p.id} value={p.name}>{p.sku}</option>)}</datalist>
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-400">Prekės ir paslaugos (kaina be PVM). Pradėkite rašyti – pasiūlysime iš katalogo.</div>
          {items.map((it, i) => (
            <ItemRow key={i} it={it} i={i} products={products} set={(v) => setItems(items.map((x, j) => (j === i ? v : x)))} remove={() => setItems(items.length > 1 ? items.filter((_, j) => j !== i) : [line()])} />
          ))}
          <div className="flex flex-wrap gap-2">
            <button data-testid="invoice-add-item-btn" onClick={() => setItems([...items, line()])} className="h-9 px-3 border border-[#3B4157] text-sm inline-flex items-center gap-2 hover:border-orange-500"><Plus className="w-4 h-4" /> Pridėti eilutę</button>
            <button data-testid="invoice-add-shipping-btn" onClick={() => setItems([...items.filter((x) => x.name.trim()), { name: "Pristatymas", code: "", qty: 1, unit: "paslauga", price: 14.88 }])} className="h-9 px-3 border border-[#3B4157] text-sm hover:border-orange-500">+ Pristatymas 18 € su PVM</button>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <label className="text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>Data</span><input data-testid="invoice-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} /></label>
          <label className="flex items-center gap-2 text-sm h-10"><input data-testid="invoice-paid-checkbox" type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} className="accent-orange-500 w-4 h-4" /> Apmokėta</label>
          {!paid && <label className="text-xs uppercase tracking-wider text-slate-400 space-y-1.5"><span>Apmokėti iki</span><input data-testid="invoice-due-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} className={input} /></label>}
        </div>
        <label className="text-xs uppercase tracking-wider text-slate-400 space-y-1.5 block"><span>Pastaba (nebūtina)</span><input data-testid="invoice-note-input" value={note} onChange={(e) => setNote(e.target.value)} className={input} /></label>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#3B4157] pt-4">
          <div data-testid="invoice-totals" className="font-mono-x text-sm text-slate-300">Be PVM {eur(net)} · PVM {eur(vat)} · <span className="text-orange-500 font-bold">Iš viso {eur(net + vat)}</span></div>
          <label className="flex items-center gap-2 text-sm"><input data-testid="invoice-send-checkbox" type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} className="accent-orange-500 w-4 h-4" /> Siųsti klientui el. paštu</label>
        </div>
        <button data-testid="invoice-save-btn" onClick={save} disabled={busy}
          className="w-full h-12 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Išrašyti sąskaitą</button>
      </DialogContent>
    </Dialog>
  );
}
